import { ConstructionType, ProjectCategoryDef, UserProfile } from '../types';
import {
    CompanyConfigRow, CompanySelf, fetchCompanyRows, pickCompanySettingsBlock,
} from './companyInvoiceConfig';
import { normalizeCategoryDefs, setCompanyProjectCategories } from './projectCategories';

/**
 * Die Projekt-Kategorisierung (Kategorie → Unterkategorie, Bauart) gilt fuer
 * die FIRMA — „das muss ueber alle Projekte laufen" (Betreiber 2026-09-15).
 *
 * Gepflegt wird sie im Firmenbereich, also in profiles.settings des Admins,
 * der sie eintraegt. Gelesen wuerde sie sonst nur aus dem Profil des
 * ANGEMELDETEN Nutzers: jeder Mitarbeiter saehe den eingebauten Standard und
 * keine Bauart. Dieselbe Luecke hatte der Rechnungsblock; dieselbe Loesung:
 * beim Anmelden den Block der Firma laden (companyInvoiceConfig.ts).
 *
 * Beide Listen werden GETRENNT gewaehlt: ein Admin kann die Kategorien
 * gepflegt haben, ein anderer die Bauarten.
 */
export interface CompanyProjectCatalog {
    projectCategories?: ProjectCategoryDef[];
    constructionTypes?: ConstructionType[];
}

const kategorienAus = (row: CompanyConfigRow): ProjectCategoryDef[] | undefined => {
    const list = normalizeCategoryDefs(row.settings?.projectCategories);
    return list.length > 0 ? list : undefined;
};

const bauartenAus = (row: CompanyConfigRow): ConstructionType[] | undefined => {
    const list = row.settings?.constructionTypes;
    return Array.isArray(list) && list.length > 0 ? (list as ConstructionType[]) : undefined;
};

export const pickCompanyProjectCatalog = (rows: CompanyConfigRow[], self: CompanySelf): CompanyProjectCatalog => ({
    projectCategories: pickCompanySettingsBlock(rows, self, kategorienAus),
    constructionTypes: pickCompanySettingsBlock(rows, self, bauartenAus),
});

/**
 * Laedt den Katalog der Firma und macht ihn dem Resolver bekannt. Best
 * effort: schlaegt die Abfrage fehl, gilt der eigene Block — an einer
 * Einstellung darf die Anmeldung nicht scheitern.
 */
export const loadCompanyProjectCatalog = async (
    profile: UserProfile | null | undefined,
): Promise<CompanyProjectCatalog> => {
    const own: CompanyProjectCatalog = {
        projectCategories: profile?.settings?.projectCategories,
        constructionTypes: profile?.settings?.constructionTypes,
    };
    let result = own;
    if (profile?.company_name || (profile as any)?.company_id) {
        try {
            const picked = pickCompanyProjectCatalog(await fetchCompanyRows(), profile as CompanySelf);
            result = {
                projectCategories: picked.projectCategories ?? own.projectCategories,
                constructionTypes: picked.constructionTypes ?? own.constructionTypes,
            };
        } catch (e) {
            console.warn('Firmen-Projektkatalog nicht ladbar', e);
        }
    }
    setCompanyProjectCategories(result.projectCategories);
    return result;
};

/** Beim Abmelden: der naechste Nutzer im selben Tab erbt nichts. */
export const clearCompanyProjectCatalog = (): void => setCompanyProjectCategories(undefined);
